import React, { useContext } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Popover,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import CustomDialog from 'src/components/UI/dialog/CustomDialog';
import axios from 'src/axios';
import { APICONSTANTS, CONSTANTS } from 'src/components/Constants';
import { SnackContext, UserContext } from 'src/store/ContextStore';
import { isAnonymousOrLoading, PII_LABEL_KEYS } from '../anonymousPedigrees';
import { probandOnlyControlState } from '../labelScope';
import {
  LABEL_KEYS,
  UNIT_BY_KEY,
  sanitiseLabelOrder,
  DEFAULT_LABEL_ORDER
} from '../pedigree-tool/pedigree/labelDefinitions';
import { sanitiseValueSort } from '../pedigree-tool/pedigree/labelValueSort';
import { sanitiseValueFilter } from '../pedigree-tool/pedigree/labelValueFilter';
import {
  buildSchemaDefaults,
  resolveLabelFormatting
} from '../pedigree-tool/pedigree/resolveLabelFormatting';
import {
  CARRIER_MARK_STYLE,
  resolveCarrierStyle
} from '../pedigree-tool/carrier-status/carrierStyle';
import LabelOrderList from './LabelOrderList';
import DisplayLabelSection from './DisplayLabelSection';
import { PRESET_SETTINGS_KEYS, presetSettingsFrom } from './presetSettings';
import { pickDefault, applyPresetStack } from './useLabelPresets';

// TGCLOUD-1663 / 1673 — one modal for the whole label configuration.
//
// Before this, configuring labels meant three places on two surfaces: a preset
// selector pinned above the sidebar accordions, per-label checkboxes and B/I
// toggles inside a LABELS accordion, and the chart-behaviour switches below
// them — all reached through a button called "Family record", which reads as
// clinical information about the family rather than a display setting.
//
// The first build of this modal separated the preset picker (top) from the save
// row (bottom) and treated closing as a cancel, so saving a preset and then
// closing REVERTED the chart — the saved arrangement was only recoverable by
// re-selecting the preset in the sidebar. Two things fix that:
//
//  1. Everything that acts on a preset lives in ONE strip at the top, next to
//     the preset it acts on.
//  2. Closing KEEPS what is on screen. Editing here changes the chart; saving a
//     preset is a separate, explicit act of persistence. Those are genuinely
//     different things and the modal now says so — "Undo changes" is the only
//     way to go backwards, and it is a deliberate action rather than the
//     accidental consequence of clicking the wrong X.

const SCOPE_PILL = {
  system: { label: 'System', color: '#6b7280' },
  group: { label: 'Group', color: '#2BA9BF' },
  user: { label: 'User', color: '#7c5cbf' }
};

// House typography (matches SystemFeatures / PedigreeSetting): section label
// 11/700 uppercase, row title 14, row description 12. The first build ran at
// 11-13px, which read small against every other dialog.
const SectionLabel = ({ children, ...rest }) => (
  <Typography
    {...rest}
    sx={{
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: 'text.disabled',
      mb: 1
    }}
  >
    {children}
  </Typography>
);

// The chart-behaviour switches. Not node labels — they have no text glyph to
// format or order — but they came along with the LABELS accordion this modal
// replaces, and only two of them are preset-backed (carrierMarkStyle and
// probandOnlyLabels, via the settings blob). That split is deliberate: the rest
// are per-session view state.
//
// The pedigree-analysis overlay is deliberately NOT here: it is a single switch
// a clinician flicks on and off while reading a chart, so it lives on the
// pedigree panel where one click reaches it, not behind a configuration modal
// you open, set and forget.
const BEHAVIOUR_SWITCHES = [
  {
    key: 'showCarrierMarks',
    title: 'Carrier marks',
    sub: 'Mark carriers with the disease colour'
  },
  {
    key: 'standardNomenclature',
    title: 'Standard nomenclature',
    sub: 'Roman generation numerals and person numbers'
  },
  {
    key: 'showRecordStatus',
    title: 'Record status',
    sub: 'Show the record-status marker on each person'
  }
];

// Proband-only is NOT in that list because it is conditional in two ways that
// the others are not (labelScope.probandOnlyControlState): it is withheld
// entirely on an anonymous deployment — there is no proband identity to
// preserve there, so offering it would imply something the deployment does not
// permit — and with no proband designated it hides nobody, which it has to SAY
// rather than silently doing nothing.
const PROBAND_ONLY = {
  key: 'probandOnlyLabels',
  title: 'Identify the proband only',
  sub: 'Hide identifying labels on everyone else'
};

// TGCLOUD-1791 — the family note on the chart.
//
// Deliberately NOT a label key. `familyNote` is a FAMILY-level string on the
// structural root (ROOT_IDENTITY_FIELDS classifies it alongside familyId and
// pedigreeConfig), so it has no node to hang under, no position in the per-node
// y-stack and nothing to reorder. labelDefinitions declares itself the source of
// truth for every NODE label and states that chart-level switches live here
// instead; adding a chart-wide item to that catalogue would put it in the
// draggable row list and in DEFAULT_LABEL_ORDER, and would fail the invariant
// that every label key belongs to exactly one orderable unit.
//
// Preset-backed rather than session-only (it is in PRESET_SETTINGS_KEYS,
// alongside probandOnlyLabels): a site that puts family notes on its charts
// wants that in a System preset, not re-ticked on every open.
//
// Defaults OFF. The note is free text that nothing redacts — it is absent from
// both PII_PERSON_FIELDS and PII_LABEL_KEYS, so proband-only mode will not
// suppress it and an anonymous-pedigrees deployment will export it verbatim.
// Off by default means nothing changes until someone asks for it, and the
// sub-label says what turning it on does.
const FAMILY_NOTE = {
  key: 'showFamilyNote',
  title: 'Family note on the chart',
  sub: 'Print the family note above the pedigree, and in exports'
};

// Does the working state still match the preset it came from? Compared against a
// snapshot taken when a preset was last applied or saved, rather than by
// re-deriving the preset's product: `settings` is spread WHOLESALE on apply, so a
// preset saved before labelOrder/valueSort existed leaves the previous value in
// place, and a naive re-derivation reads "edited" the instant it is applied.
// Two key sets, because "is there anything to undo?" and "does the chart still
// match its preset?" are different questions and conflating them gets one of
// them wrong either way.
//
// PRESET_KEYS is what a preset actually stores. Only these can make the chart
// differ from the preset it came from, so only these drive the "edited" marker
// — marking the chart edited over a switch no preset can hold would promise a
// Save that does not persist it.
const PRESET_KEYS = [...LABEL_KEYS, ...PRESET_SETTINGS_KEYS];

// UNDO_KEYS adds the session-only chart switches: they are not saved, but they
// ARE edits the user just made and can reasonably take back. Leaving them out
// left Undo disabled after toggling three of the four Chart behaviour switches.
const UNDO_KEYS = [
  ...PRESET_KEYS,
  'showCarrierMarks',
  'standardNomenclature',
  'showRecordStatus',
  'showAnalysisOverlay',
  'diseases',
  'phenotypes',
  'genotypes'
];

// The formatting map has two shapes for the same meaning: the resolver writes
// both members ({bold, italic}), while a toggle can leave one absent and drops
// the entry entirely when nothing is on. Compare what it MEANS — otherwise
// turning bold off and on again left the chart "edited" forever.
const normaliseFormatting = (formatting) => {
  const out = {};
  Object.entries(formatting || {}).forEach(([k, v]) => {
    if (v?.bold || v?.italic) out[k] = { bold: !!v.bold, italic: !!v.italic };
  });
  // Key order follows insertion, so sort for a stable comparison.
  return Object.keys(out)
    .sort()
    .map((k) => `${k}:${out[k].bold ? 'b' : ''}${out[k].italic ? 'i' : ''}`)
    .join('|');
};

const sameConfig = (a, b, keys) => {
  if (!a || !b) return a === b;
  if (normaliseFormatting(a.formatting) !== normaliseFormatting(b.formatting)) {
    return false;
  }
  return keys.every(
    (k) => JSON.stringify(a[k] ?? null) === JSON.stringify(b[k] ?? null)
  );
};

const LabelPresetModal = ({
  open,
  handleClose,
  labelsSet,
  setLabelsSet,
  scoped,
  reloadPresets,
  pedigreeSetting,
  appliedPresetId,
  setAppliedPresetId,
  gNumber,
  primaryExternalId,
  hasExternalIds,
  diagnoses,
  probandPid
}) => {
  const { user } = useContext(UserContext);
  const { setSnack } = useContext(SnackContext);

  // The snack contract is an OBJECT ({status, msg, severity}) — the consumer
  // reads snack.status/msg/severity. The first build passed two positional
  // arguments, so every confirmation and the permission refusal silently did
  // nothing at all.
  const snack = (msg, severity) =>
    setSnack({ status: true, msg, severity: severity || 'success' });

  const anonymous = isAnonymousOrLoading(pedigreeSetting);
  // Under an anonymous deployment the PII labels are clamped away at the render
  // boundary regardless of what any preset says, so offering their checkboxes
  // here would be offering controls that do nothing.
  const hiddenKeys = anonymous ? PII_LABEL_KEYS : [];

  // Two baselines, deliberately distinct:
  //  - openRef: what the chart looked like when the modal opened. "Undo changes"
  //    returns here, and it is reset after a successful save so undo can never
  //    reach back past something the user chose to keep.
  //  - appliedRef: the state the applied preset produced. Drives the "edited"
  //    indicator.
  const openRef = React.useRef(null);
  const appliedRef = React.useRef(null);
  // Which preset was applied at open, so Undo restores the SELECTION too. Undoing
  // the state while leaving appliedPresetId pointing at a different preset made
  // the next Save overwrite that preset with this configuration.
  const openPresetIdRef = React.useRef(null);
  // Seeded during the FIRST render, not in an effect: `dirty` is read while
  // rendering, and an effect runs afterwards — so on the opening frame the
  // baselines were still null, every comparison read "edited", and writing a
  // ref does not re-render to correct it. Lazy ref initialisation is the right
  // shape for a value derived from the first render.
  if (open && openRef.current === null) {
    openRef.current = labelsSet;
    appliedRef.current = labelsSet;
    openPresetIdRef.current = appliedPresetId ?? null;
  }

  const [saveAsOpen, setSaveAsOpen] = React.useState(false);
  const [presetName, setPresetName] = React.useState('');
  const [saveScope, setSaveScope] = React.useState('user');
  const [makeDefault, setMakeDefault] = React.useState(true);
  const [manageAnchor, setManageAnchor] = React.useState(null);
  const [saving, setSaving] = React.useState(false);

  // Scopes this user may manage. Everyone owns their `user` presets; group
  // presets are managed by Genetics Service, Genetics Service Admin and System
  // Admin; system presets by System Admin only, and can never be deleted. The
  // server enforces all of this too — this only keeps the UI honest.
  const userType = user?.userType;
  const canEditGroup =
    userType === CONSTANTS.userTypes.ADMIN ||
    userType === CONSTANTS.userTypes.GENETICS_SERVICE_ADMIN ||
    userType === CONSTANTS.userTypes.GENETICS;
  const canEditSystem = userType === CONSTANTS.userTypes.ADMIN;
  const writableScopes = [
    'user',
    ...(canEditGroup ? ['group'] : []),
    ...(canEditSystem ? ['system'] : [])
  ];
  const canWriteScope = (scope) => writableScopes.includes(scope);

  const allPresets = React.useMemo(
    () => [
      ...(scoped?.system || []).map((p) => ({ ...p, scope: 'system' })),
      ...(scoped?.group || []).map((p) => ({ ...p, scope: 'group' })),
      ...(scoped?.user || []).map((p) => ({ ...p, scope: 'user' }))
    ],
    [scoped]
  );

  const applied = allPresets.find((p) => p._id === appliedPresetId) || null;
  // TWO predicates, because there are two baselines and they answer different
  // questions. `edited` drives the marker beside the preset name: does the chart
  // still match the preset it came from? `canUndo` drives the Undo button: is
  // there anything to go back to? Gating Undo on `edited` disabled it precisely
  // when the user most needed it — after switching preset, the chart matches the
  // NEW preset, so `edited` is false while the state at open is still lost.
  const edited = !sameConfig(labelsSet, appliedRef.current, PRESET_KEYS);
  const canUndo =
    !sameConfig(labelsSet, openRef.current, UNDO_KEYS) ||
    (openPresetIdRef.current ?? null) !== (appliedPresetId ?? null);
  const order = sanitiseLabelOrder(labelsSet?.labelOrder);

  // ── mutations on the live chart state ──────────────────────────────────────

  const setVisible = (unit, checked) => {
    const keys = UNIT_BY_KEY[unit]?.keys || [];
    setLabelsSet((prev) => {
      const next = { ...prev };
      keys.forEach((k) => {
        next[k] = checked;
      });
      // Coupled pair carried over from the sidebar: a clinically-confirmed tick
      // is drawn ON a disease row, so it cannot show without one.
      if (keys.includes('diseaseWithAge')) next.confirmedDiagnosis = checked;
      if (keys.includes('confirmedDiagnosis') && checked) {
        next.diseaseWithAge = true;
      }
      return next;
    });
  };

  // One key within a multi-key row, so the parts of a shared line stay
  // independently configurable (genotype symbol without its ID, for instance).
  const setKeyVisible = (unit, key, checked) =>
    setLabelsSet((prev) => {
      const next = { ...prev, [key]: checked };
      // Same coupling as the row control: the confirmed-diagnosis tick is drawn
      // ON a disease row, so it cannot show without one.
      if (key === 'diseaseWithAge' && !checked) next.confirmedDiagnosis = false;
      if (key === 'confirmedDiagnosis' && checked) next.diseaseWithAge = true;
      return next;
    });

  // Bold/italic for the label keys the clicked control speaks for — ONE key
  // from a part row, all of a group's keys from its row-level pair.
  //
  // This used to take a unit and look its keys up, which meant every control
  // wrote the whole group and the parts of a grouped row could not be styled
  // apart (TGCLOUD-1761). The caller naming its own target keys is what makes
  // "bold the Carrier Status only" expressible at all.
  const setFormat = (keys, attr, value) => {
    setLabelsSet((prev) => {
      const formatting = { ...(prev?.formatting || {}) };
      (keys || []).forEach((k) => {
        const current = { ...(formatting[k] || {}), [attr]: value };
        // Keep the map sparse: an entry with no active style is dropped, so an
        // all-off map is `{}` and adds nothing to the render.
        if (current.bold || current.italic) formatting[k] = current;
        else delete formatting[k];
      });
      return { ...prev, formatting };
    });
  };

  const setSort = (unit, patch) => {
    setLabelsSet((prev) => {
      const valueSort = { ...(prev?.valueSort || {}) };
      valueSort[unit] = { ...(valueSort[unit] || {}), ...patch };
      return { ...prev, valueSort: sanitiseValueSort(valueSort) };
    });
  };

  // TGCLOUD-1792 — which of a label's records print. Mirrors setSort exactly,
  // including the sanitise on the way in: `sameConfig` compares these blobs with
  // JSON.stringify, so normalising key order here is what stops the "edited"
  // chip firing on a filter change that did not actually change anything.
  const setFilter = (unit, patch) => {
    setLabelsSet((prev) => {
      const valueFilter = { ...(prev?.valueFilter || {}) };
      valueFilter[unit] = { ...(valueFilter[unit] || {}), ...patch };
      return { ...prev, valueFilter: sanitiseValueFilter(valueFilter) };
    });
  };

  // Carrier mark STYLE (border vs Bennett-2022 vertical line). This governs only
  // the gene→disease carrier marks; the legacy +/- genotype Carrier-Status
  // annotation is a separate pipeline and deliberately untouched here.
  //
  // Vertical line is only meaningful when a single disease is shown — with more
  // than one there is no way to say which the line refers to — so it disables,
  // and the renderer independently falls back to border. Restored here from the
  // sidebar panel this modal replaced: carrierMarkStyle is read by the canvas
  // and stored in presets, so losing its control left a saved setting nobody
  // could change.
  const visibleDiseaseCount =
    labelsSet?.diseases === false
      ? 0
      : (diagnoses || []).filter(
          (d) => labelsSet?.diseasesMap?.[d.id] !== false
        ).length;
  const preferredCarrierStyle =
    labelsSet?.carrierMarkStyle || CARRIER_MARK_STYLE.border;
  const { fallbackActive } = resolveCarrierStyle(
    preferredCarrierStyle,
    visibleDiseaseCount
  );
  const verticalLineEnabled = visibleDiseaseCount === 1;

  // Withheld on an anonymous deployment; inert-with-an-explanation when no
  // proband is designated, rather than silently hiding nobody.
  const probandOnly = probandOnlyControlState({ anonymous, probandPid });

  const setOrder = (nextOrder) =>
    setLabelsSet((prev) => ({ ...prev, labelOrder: nextOrder }));

  const setBehaviour = (key, value) =>
    setLabelsSet((prev) => ({ ...prev, [key]: value }));

  // ── preset actions ─────────────────────────────────────────────────────────

  // The preset stack that applying `chosen` should produce. Mirrors baseFor:
  // whatever baseFor(scope) uses as the diff base is exactly what sits beneath
  // the chosen preset here, so applying then saving round-trips.
  const stackFor = (chosen) => {
    const sys = pickDefault(scoped?.system);
    const grp = pickDefault(scoped?.group);
    if (!chosen) return { system: sys, group: grp, user: null };
    if (chosen.scope === 'system') {
      return { system: chosen, group: null, user: null };
    }
    if (chosen.scope === 'group') {
      return { system: sys, group: chosen, user: null };
    }
    return { system: sys, group: grp, user: chosen };
  };

  const baseFor = (scope) => {
    // The inherited base BELOW the target scope, so a saved preset stores only
    // what it actually changes.
    const sys = pickDefault(scoped?.system);
    const grp = pickDefault(scoped?.group);
    return resolveLabelFormatting(
      buildSchemaDefaults(),
      scope === 'system' ? null : sys,
      scope === 'group' || scope === 'system' ? null : grp,
      null
    );
  };

  const applyStack = (chosen) => {
    // Computed from the current labelsSet rather than inside a setState updater:
    // an updater must be pure, and React can invoke it twice. The baseline has
    // to move with the state, so doing both here keeps them in step.
    // A chosen preset occupies ITS OWN layer, with only the more general
    // defaults beneath it. Passing it as the `user` layer while also passing the
    // group default meant picking a non-default group preset showed the group
    // default MERGED with it — not the preset the user asked for — and the save
    // diff (baseFor) excluded that extra layer, so the two disagreed.
    const next = applyPresetStack(labelsSet, stackFor(chosen));
    // The applied baseline is the state the preset produced, so the "edited"
    // indicator starts clean straight after an apply.
    appliedRef.current = next;
    setLabelsSet(next);
  };

  const applySelected = (id) => {
    setAppliedPresetId(id || null);
    setSaveAsOpen(false);
    if (!id) {
      applyStack(null);
      return;
    }
    const chosen = allPresets.find((p) => p._id === id);
    if (chosen) applyStack(chosen);
  };

  const undoChanges = () => {
    if (!openRef.current) return;
    const baseline = openRef.current;
    appliedRef.current = baseline;
    setLabelsSet(baseline);
    // The selection is part of what is being undone: leaving it pointing at
    // another preset would aim the next Save at the wrong document.
    setAppliedPresetId(openPresetIdRef.current ?? null);
    setSaveAsOpen(false);
  };

  const overridesFrom = (scope) => {
    const base = baseFor(scope);
    const overrides = {};
    LABEL_KEYS.forEach((key) => {
      // On an anonymous deployment PedigreeBox hands the modal the CLAMPED set,
      // so every PII key reads false — from checkboxes the user is never shown.
      // Storing that would bake "names hidden" into a preset nobody chose to
      // make, so those keys are left to inherit instead.
      if (hiddenKeys.includes(key)) return;
      const visible = !!labelsSet?.[key];
      const fmt = labelsSet?.formatting?.[key] || {};
      const b = base[key] || {};
      const patch = {};
      // Sparse: only what differs from the inherited base is stored.
      if (visible !== !!b.visible) patch.visible = visible;
      if (!!fmt.bold !== !!b.bold) patch.bold = !!fmt.bold;
      if (!!fmt.italic !== !!b.italic) patch.italic = !!fmt.italic;
      if (Object.keys(patch).length) overrides[key] = patch;
    });
    return overrides;
  };

  const postPreset = ({ scope, name, isDefault }) => {
    const body = {
      scope,
      name,
      overrides: overridesFrom(scope),
      settings: presetSettingsFrom(
        labelsSet,
        // Clamped: on an anonymous deployment the chart always shows the G
        // number, so a preset saved there must not persist 'primary-external'
        // and surface it on a deployment that is not anonymous.
        anonymous ? 'g-number' : labelsSet?.displayLabelMode || 'g-number'
      )
    };
    // Omitted rather than false when saving in place: the controller only writes
    // fields that are defined, so leaving it out PRESERVES whatever default flag
    // the stored preset already had. Updating a preset should not quietly
    // promote or demote it.
    if (isDefault !== undefined) body.isDefault = isDefault;

    setSaving(true);
    return axios
      .post(APICONSTANTS.labelDisplayPresets, body)
      .then(({ data }) => {
        if (data?._id) setAppliedPresetId(data._id);
        // Saving is a decision to keep: EVERY baseline moves to it — including
        // the selection, or Undo would stay enabled forever after a Save as…
        // and would then re-point the selection at the preset that happened to
        // be applied when the modal opened.
        appliedRef.current = labelsSet;
        openRef.current = labelsSet;
        if (data?._id) openPresetIdRef.current = data._id;
        return reloadPresets();
      })
      .then(() => {
        setSaveAsOpen(false);
        snack(`Saved "${name}"`, 'success');
      })
      .catch((err) => {
        snack(
          err?.response?.status === 403
            ? 'You do not have permission to save at that scope'
            : 'Could not save the preset',
          'error'
        );
      })
      .finally(() => setSaving(false));
  };

  // SAVE updates the applied preset where the caller may write its scope.
  // Otherwise — a system preset, or nothing applied because the chart is on its
  // inherited default — there is nothing to update, so it opens the save-as form
  // prefilled instead of refusing.
  const handleSaveInPlace = () => {
    if (applied && canWriteScope(applied.scope)) {
      return postPreset({ scope: applied.scope, name: applied.name });
    }
    setPresetName(applied ? `Copy of ${applied.name}` : 'My labels');
    setSaveScope(writableScopes[0]);
    setMakeDefault(true);
    setSaveAsOpen(true);
    return Promise.resolve();
  };

  const openSaveAs = () => {
    setPresetName(applied ? `Copy of ${applied.name}` : 'My labels');
    setSaveScope(
      applied && canWriteScope(applied.scope)
        ? applied.scope
        : writableScopes[0]
    );
    setMakeDefault(true);
    setSaveAsOpen(true);
  };

  const handleSaveAs = () => {
    const name = (presetName || '').trim();
    if (!name) {
      snack('Give the preset a name before saving', 'warning');
      return Promise.resolve();
    }
    return postPreset({ scope: saveScope, name, isDefault: makeDefault });
  };

  const setAsDefault = (preset) =>
    axios
      .post(`${APICONSTANTS.labelDisplayPresets}/${preset._id}/default`)
      .then(() => reloadPresets())
      // Names the scope: the unique index is {scope, scopeRef, name}, so the same
      // name legitimately exists at two scopes and both appear in this list.
      .then(() =>
        snack(
          `"${preset.name}" is now the ${SCOPE_PILL[
            preset.scope
          ].label.toLowerCase()} default`,
          'success'
        )
      )
      .catch(() => snack('Could not set that default', 'error'));

  const deletePreset = (preset) =>
    axios
      .delete(`${APICONSTANTS.labelDisplayPresets}/${preset._id}`)
      .then(() => reloadPresets())
      .then((fresh) => {
        if (appliedPresetId !== preset._id) return;
        // Re-resolve from the list the reload returned, not the one captured in
        // this closure — that one still contains the preset just deleted, and
        // if it was a scope default it would be layered back in.
        setAppliedPresetId(null);
        const next = applyPresetStack(labelsSet, {
          system: pickDefault(fresh?.system ?? scoped?.system),
          group: pickDefault(fresh?.group ?? scoped?.group),
          user: null
        });
        appliedRef.current = next;
        setLabelsSet(next);
      })
      .then(() => snack(`Deleted "${preset.name}"`, 'success'))
      .catch(() => snack('Could not delete that preset', 'error'));

  const canDelete = (p) =>
    p.scope !== 'system' &&
    (p.scope === 'user' || (p.scope === 'group' && canEditGroup));
  // The star was never gated, so a user without the scope was offered
  // "set as default" and got a 403 for taking it.
  const canSetDefault = (p) => !p.isDefault && canWriteScope(p.scope);

  return (
    <CustomDialog
      open={open}
      // Every close path — Done, the X, Escape, clicking away — KEEPS what is on
      // screen. Editing here IS the chart's configuration; persisting it to a
      // preset is the separate, explicit act above.
      handleClose={handleClose}
      allowBackgroundClose
      title="Pedigree labels"
      maxWidth={940}
      footerAction={
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            gap: 1
          }}
        >
          <Button
            size="small"
            onClick={undoChanges}
            disabled={!canUndo}
            sx={{ mr: 'auto', fontSize: 13 }}
          >
            Undo changes
          </Button>
          <Button variant="contained" color="secondary" onClick={handleClose}>
            Done
          </Button>
        </Box>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Preset strip — everything that acts on a preset, in one place */}
        <Box>
          <SectionLabel>Preset</SectionLabel>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              flexWrap: 'wrap'
            }}
          >
            <TextField
              select
              size="small"
              value={appliedPresetId || ''}
              onChange={(e) => applySelected(e.target.value)}
              sx={{
                flex: 1,
                minWidth: 220,
                '& .MuiInputBase-input': { fontSize: 14 }
              }}
              data-va-anchor="pedigree.labels.presetBar"
              slotProps={{
                select: { 'aria-label': 'Applied preset' },
                htmlInput: { 'aria-label': 'Applied preset' }
              }}
            >
              <MenuItem value="" sx={{ fontSize: 14 }}>
                <em>Inherited default</em>
              </MenuItem>
              {allPresets.map((p) => (
                <MenuItem key={p._id} value={p._id} sx={{ fontSize: 14 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      width: '100%'
                    }}
                  >
                    <span>{p.name}</span>
                    <Chip
                      size="small"
                      label={SCOPE_PILL[p.scope].label}
                      sx={{
                        height: 18,
                        fontSize: 11,
                        color: '#fff',
                        backgroundColor: SCOPE_PILL[p.scope].color
                      }}
                    />
                    {p.isDefault && (
                      <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>
                        (default)
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </TextField>

            {edited && (
              <Chip
                size="small"
                label="edited"
                sx={{ height: 22, fontSize: 11, fontStyle: 'italic' }}
              />
            )}

            <Tooltip
              title={
                applied && canWriteScope(applied.scope)
                  ? `Update "${applied.name}"`
                  : 'Save these settings as a preset'
              }
              arrow
            >
              <span style={{ display: 'inline-flex' }}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={saving}
                  onClick={handleSaveInPlace}
                >
                  Save
                </Button>
              </span>
            </Tooltip>
            <Button size="small" onClick={openSaveAs} disabled={saving}>
              Save as…
            </Button>
            <Button
              size="small"
              onClick={(e) => setManageAnchor(e.currentTarget)}
            >
              Manage
            </Button>
          </Box>

          {saveAsOpen && (
            <Box
              sx={{
                mt: 1.5,
                p: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                backgroundColor: 'rgba(0,0,0,0.02)'
              }}
            >
              <SectionLabel>Save as a new preset</SectionLabel>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                  flexWrap: 'wrap'
                }}
              >
                {writableScopes.length > 1 && (
                  <TextField
                    select
                    size="small"
                    label="Scope"
                    value={saveScope}
                    onChange={(e) => setSaveScope(e.target.value)}
                    sx={{
                      width: 150,
                      '& .MuiInputBase-input': { fontSize: 14 }
                    }}
                  >
                    {writableScopes.map((s) => (
                      <MenuItem key={s} value={s} sx={{ fontSize: 14 }}>
                        {SCOPE_PILL[s].label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                <TextField
                  size="small"
                  label="Preset name"
                  value={presetName}
                  onChange={(e) => setPresetName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSaveAs();
                    }
                  }}
                  data-va-anchor="pedigree.labels.presetName"
                  sx={{
                    flex: 1,
                    minWidth: 200,
                    '& .MuiInputBase-input': { fontSize: 14 }
                  }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={makeDefault}
                      onChange={(e) => setMakeDefault(e.target.checked)}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: 14 }}>
                      Set as default for this scope
                    </Typography>
                  }
                />
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  disabled={saving}
                  onClick={handleSaveAs}
                >
                  Save preset
                </Button>
                <Button size="small" onClick={() => setSaveAsOpen(false)}>
                  Cancel
                </Button>
              </Box>
            </Box>
          )}
        </Box>

        <Divider />

        {/* Labels: visibility, order, style, value order */}
        <Box>
          <SectionLabel>Labels</SectionLabel>
          <LabelOrderList
            labelsSet={labelsSet}
            order={order}
            hiddenKeys={hiddenKeys}
            onOrderChange={setOrder}
            onToggleVisible={setVisible}
            onToggleKey={setKeyVisible}
            onToggleFormat={setFormat}
            onSortChange={setSort}
            onFilterChange={setFilter}
          />
          <Button
            size="small"
            onClick={() => setOrder(DEFAULT_LABEL_ORDER)}
            sx={{ mt: 0.5, fontSize: 12 }}
          >
            Reset order
          </Button>
        </Box>

        <DisplayLabelSection
          labelsSet={labelsSet}
          setLabelsSet={setLabelsSet}
          anonymous={anonymous}
          gNumber={gNumber}
          primaryExternalId={primaryExternalId}
          hasExternalIds={hasExternalIds}
          SectionLabel={SectionLabel}
        />

        <Divider />

        {/* Chart behaviour */}
        <Box>
          <SectionLabel>Chart behaviour</SectionLabel>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              columnGap: 2
            }}
          >
            {BEHAVIOUR_SWITCHES.map((s) => (
              <FormControlLabel
                key={s.key}
                control={
                  <Switch
                    size="small"
                    checked={!!labelsSet?.[s.key]}
                    onChange={(e) => setBehaviour(s.key, e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography sx={{ fontSize: 14 }}>{s.title}</Typography>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                      {s.sub}
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', mb: 1 }}
              />
            ))}
          </Box>

          {probandOnly.offered && (
            <Box sx={{ pt: 0.5 }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={!!labelsSet?.probandOnlyLabels}
                    onChange={(e) =>
                      setBehaviour(PROBAND_ONLY.key, e.target.checked)
                    }
                  />
                }
                label={
                  <Box>
                    <Typography sx={{ fontSize: 14 }}>
                      {PROBAND_ONLY.title}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                      {PROBAND_ONLY.sub}
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', mb: 0.5 }}
              />
              {probandOnly.warning && (
                <Typography
                  sx={{ fontSize: 12, color: 'warning.main', pl: 1.5 }}
                >
                  {probandOnly.warning}
                </Typography>
              )}
            </Box>
          )}

          <Box sx={{ pt: 0.5 }}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={!!labelsSet?.showFamilyNote}
                  onChange={(e) =>
                    setBehaviour(FAMILY_NOTE.key, e.target.checked)
                  }
                />
              }
              label={
                <Box>
                  <Typography sx={{ fontSize: 14 }}>
                    {FAMILY_NOTE.title}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                    {FAMILY_NOTE.sub}
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', mb: 0.5 }}
            />
            {!!labelsSet?.showFamilyNote && (
              <Typography sx={{ fontSize: 12, color: 'warning.main', pl: 1.5 }}>
                The family note is free text and is not redacted — it will
                appear on the chart and in every export exactly as written.
              </Typography>
            )}
          </Box>

          {!!labelsSet?.showCarrierMarks && (
            <Box sx={{ pl: 1.5, pt: 0.5 }}>
              <Typography
                sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}
              >
                Carrier mark style
              </Typography>
              <RadioGroup
                row
                value={preferredCarrierStyle}
                onChange={(e) =>
                  setLabelsSet((prev) => ({
                    ...prev,
                    carrierMarkStyle: e.target.value
                  }))
                }
                sx={{ pl: 0.5 }}
              >
                <FormControlLabel
                  value={CARRIER_MARK_STYLE.border}
                  control={<Radio size="small" />}
                  label="Border"
                  sx={{ '& .MuiFormControlLabel-label': { fontSize: 14 } }}
                />
                <Tooltip
                  title={
                    verticalLineEnabled
                      ? ''
                      : 'Vertical line style is available when only one disease is shown on the pedigree.'
                  }
                  placement="top"
                  arrow
                >
                  {/* span wrapper so the tooltip works while the radio is disabled */}
                  <span
                    style={{ display: 'inline-flex', width: 'fit-content' }}
                  >
                    <FormControlLabel
                      value={CARRIER_MARK_STYLE.verticalLine}
                      disabled={!verticalLineEnabled}
                      control={<Radio size="small" />}
                      label="Vertical line"
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: 14 } }}
                    />
                  </span>
                </Tooltip>
              </RadioGroup>
              {fallbackActive && (
                <Typography
                  sx={{
                    fontSize: 12,
                    color: 'warning.main',
                    mt: 0.5,
                    pl: 0.5
                  }}
                >
                  Showing as Border because more than one disease is visible.
                  Vertical line will return when filtered to a single disease.
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>

      <Popover
        open={!!manageAnchor}
        anchorEl={manageAnchor}
        onClose={() => setManageAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 1, minWidth: 340 }}>
          <SectionLabel>Manage presets</SectionLabel>
          {!allPresets.length && (
            <Typography sx={{ fontSize: 13, color: 'text.secondary', p: 1 }}>
              No presets saved yet.
            </Typography>
          )}
          {allPresets.map((p) => (
            <Box
              key={p._id}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}
            >
              <Chip
                size="small"
                label={SCOPE_PILL[p.scope].label}
                sx={{
                  height: 18,
                  fontSize: 11,
                  color: '#fff',
                  backgroundColor: SCOPE_PILL[p.scope].color
                }}
              />
              <Typography sx={{ fontSize: 14, flex: 1, minWidth: 0 }}>
                {p.name}
              </Typography>
              <Tooltip
                title={
                  p.isDefault
                    ? 'Current default'
                    : canSetDefault(p)
                    ? 'Set as default'
                    : `You cannot change the ${SCOPE_PILL[
                        p.scope
                      ].label.toLowerCase()} default`
                }
                arrow
              >
                <span style={{ display: 'inline-flex' }}>
                  <IconButton
                    size="small"
                    disabled={!canSetDefault(p)}
                    onClick={() => setAsDefault(p)}
                    aria-label={`Set ${p.name} as default`}
                  >
                    {p.isDefault ? (
                      <StarIcon sx={{ fontSize: 16, color: '#f0b429' }} />
                    ) : (
                      <StarBorderIcon sx={{ fontSize: 16 }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  canDelete(p) ? 'Delete' : 'System presets cannot be deleted'
                }
                arrow
              >
                <span style={{ display: 'inline-flex' }}>
                  <IconButton
                    size="small"
                    disabled={!canDelete(p)}
                    onClick={() => deletePreset(p)}
                    aria-label={`Delete ${p.name}`}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          ))}
        </Box>
      </Popover>
    </CustomDialog>
  );
};

export default LabelPresetModal;
